/**
 * Tree Visualization Component
 * 
 * Renders the Opportunity Solution Tree as an interactive, hierarchical tree.
 * Supports:
 * - Expandable/collapsible nodes
 * - Visual distinction between node types
 * - Click to select and edit
 * - Add child nodes
 */

import { useState, useMemo } from 'react';
import {
  Target,
  Lightbulb,
  Wrench,
  FlaskConical,
  ChevronDown,
  ChevronRight,
  Plus,
  Crosshair,
  MessageSquareQuote,
  CheckCircle2,
  XCircle,
  Clock,
  HelpCircle,
  Flag,
} from 'lucide-react';
import type { 
  OSTNode, 
  TreeNodeWithChildren, 
  NodeType,
  ValidationStatus,
  ExperimentStatus,
} from '../types';

interface TreeVisualizationProps {
  nodes: OSTNode[];
  selectedNodeId: string | null;
  onSelectNode: (nodeId: string) => void;
  onAddChild: (parentId: string, type: NodeType) => void;
  onToggleCollapse: (nodeId: string) => void;
  /** Strategy (outcome) row — link workspace strategic objective onto the root. */
  onPickStrategicObjective?: (outcomeNodeId: string) => void;
  /** Strategy (outcome) row — link a desired outcome artifact onto the root. */
  onPickDesiredOutcome?: (outcomeNodeId: string) => void;
}

// Build tree structure from flat node list
function buildTree(nodes: OSTNode[]): TreeNodeWithChildren[] {
  const nodeMap = new Map<string, OSTNode>();
  const childrenMap = new Map<string, OSTNode[]>();
  
  // Index nodes
  nodes.forEach(node => {
    nodeMap.set(node.id, node);
    if (!childrenMap.has(node.id)) {
      childrenMap.set(node.id, []);
    }
  });
  
  // Build parent-child relationships
  nodes.forEach(node => {
    if (node.parentId) {
      const siblings = childrenMap.get(node.parentId) || [];
      siblings.push(node);
      childrenMap.set(node.parentId, siblings);
    }
  });
  
  // Recursively build tree
  function buildSubtree(node: OSTNode, depth: number): TreeNodeWithChildren {
    const children = childrenMap.get(node.id) || [];
    return {
      node,
      children: children.map(child => buildSubtree(child, depth + 1)),
      depth,
      isTargeted: node.type === 'opportunity' && (node as any).isTargeted,
    };
  }
  
  // Find root nodes (outcomes)
  const roots = nodes.filter(n => n.parentId === null);
  return roots.map(root => buildSubtree(root, 0));
}

// Node type configurations
const nodeTypeConfig: Record<NodeType, { 
  icon: typeof Target; 
  color: string;
  bgColor: string;
  borderColor: string;
  label: string;
}> = {
  outcome: {
    icon: Target,
    color: 'text-consulting-slate-blue-700 dark:text-consulting-slate-blue-400',
    bgColor: 'bg-consulting-slate-blue-50 dark:bg-consulting-slate-blue-900/25',
    borderColor: 'border-consulting-slate-blue-200 dark:border-consulting-slate-blue-800',
    label: 'Strategy',
  },
  opportunity: {
    icon: Lightbulb,
    color: 'text-[var(--mustard)]',
    bgColor: 'bg-[var(--mustard-dim)]',
    borderColor: 'border-[var(--mustard)]',
    label: 'Opportunity',
  },
  solution: {
    icon: Wrench,
    color: 'text-[var(--text-sec)]',
    bgColor: 'bg-[var(--slate-dim)]',
    borderColor: 'border-[var(--slate)]',
    label: 'Solution',
  },
  experiment: {
    icon: FlaskConical,
    color: 'text-[var(--green)]',
    bgColor: 'bg-[var(--green-dim)]',
    borderColor: 'border-[var(--green)]',
    label: 'Experiment',
  },
};

// Allowed child types for each node type
const allowedChildren: Record<NodeType, NodeType[]> = {
  outcome: ['opportunity'],
  opportunity: ['opportunity', 'solution'],
  solution: ['experiment'],
  experiment: [],
};

// Validation status indicator
function ValidationStatusBadge({ status }: { status: ValidationStatus }) {
  const configs: Record<ValidationStatus, { icon: typeof CheckCircle2; color: string; label: string }> = {
    validated: { icon: CheckCircle2, color: 'text-[var(--green)]', label: 'Validated' },
    invalidated: { icon: XCircle, color: 'text-[var(--coral)]', label: 'Invalidated' },
    in_progress: { icon: Clock, color: 'text-[var(--mustard)]', label: 'In Progress' },
    not_started: { icon: HelpCircle, color: 'text-[var(--text-ter)]', label: 'Not Started' },
    needs_more_data: { icon: HelpCircle, color: 'text-[var(--text-ter)]', label: 'Needs Data' },
  };
  
  const config = configs[status];
  const Icon = config.icon;
  
  return (
    <div className={`flex items-center gap-1 text-xs ${config.color}`}>
      <Icon className="h-3 w-3" />
      <span>{config.label}</span>
    </div>
  );
}

// Experiment status indicator
function ExperimentStatusBadge({ status }: { status: ExperimentStatus }) {
  const configs: Record<ExperimentStatus, { color: string; bgColor: string; label: string }> = {
    completed: { color: 'text-[var(--green)] dark:text-[var(--text-ter)]', bgColor: 'bg-[var(--green-dim)]', label: 'Completed' },
    in_progress: { color: 'text-[var(--mustard)]', bgColor: 'bg-[var(--mustard-dim)]', label: 'In Progress' },
    planned: { color: 'text-[var(--text-sec)]', bgColor: 'bg-[var(--hover-tint)]', label: 'Planned' },
    inconclusive: { color: 'text-[var(--text-sec)]', bgColor: 'bg-[var(--slate-dim)]', label: 'Inconclusive' },
  };
  
  const config = configs[status];
  
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${config.bgColor} ${config.color}`}>
      {config.label}
    </span>
  );
}

// Single tree node component
function TreeNode({
  treeNode,
  selectedNodeId,
  onSelectNode,
  onAddChild,
  onToggleCollapse,
  onPickStrategicObjective,
  onPickDesiredOutcome,
}: {
  treeNode: TreeNodeWithChildren;
  selectedNodeId: string | null;
  onSelectNode: (nodeId: string) => void;
  onAddChild: (parentId: string, type: NodeType) => void;
  onToggleCollapse: (nodeId: string) => void;
  onPickStrategicObjective?: (outcomeNodeId: string) => void;
  onPickDesiredOutcome?: (outcomeNodeId: string) => void;
}) {
  const { node, children, isTargeted } = treeNode;
  const config = nodeTypeConfig[node.type];
  const Icon = config.icon;
  const isSelected = selectedNodeId === node.id;
  const isCollapsed = node.collapsed;
  const hasChildren = children.length > 0;
  const canAddChildren = allowedChildren[node.type].length > 0;
  
  const [showAddMenu, setShowAddMenu] = useState(false);
  
  return (
    <div className="relative">
      {/* Connection line to parent */}
      {node.parentId && (
        <div className="absolute -top-4 left-6 w-px h-4 bg-[var(--rule-bright)]" />
      )}
      
      {/* Node card */}
      <div
        className={`
          relative flex items-start gap-3 p-3  border-2 cursor-pointer
          transition-all duration-150 hover:shadow-md
          ${config.bgColor} ${config.borderColor}
          ${isSelected ? 'ring-2 ring-consulting-slate-blue-500 ring-offset-2' : ''}
          ${isTargeted ? 'ring-2 ring-amber-400 ring-offset-2' : ''}
        `}
        onClick={() => onSelectNode(node.id)}
      >
        {/* Expand/collapse button */}
        {hasChildren && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleCollapse(node.id);
            }}
            className="absolute -left-6 top-3 p-1 bg-[var(--bg-raised)] rounded-full shadow-sm border border-[var(--rule)] hover:bg-[var(--surface)]"
          >
            {isCollapsed ? (
              <ChevronRight className="h-3 w-3 text-[var(--text-ter)]" />
            ) : (
              <ChevronDown className="h-3 w-3 text-[var(--text-ter)]" />
            )}
          </button>
        )}
        
        {/* Icon */}
        <div className={`flex-shrink-0 p-2  bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)]/10 ${config.color}`}>
          <Icon className="h-5 w-5" />
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`text-xs font-medium ${config.color}`}>
              {config.label}
            </span>
            {isTargeted && (
              <span className="flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-[var(--mustard-dim)] text-[var(--mustard)] rounded-full">
                <Crosshair className="h-3 w-3" />
                Target
              </span>
            )}
          </div>
          
          <h4 className="font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] mt-1 line-clamp-2">
            {node.title}
          </h4>
          
          {node.description && (
            <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] mt-1 line-clamp-2">
              {node.description}
            </p>
          )}
          
          {/* Customer quote for opportunities */}
          {node.type === 'opportunity' && (node as any).customerQuote && (
            <div className="flex items-start gap-2 mt-2 p-2 bg-[var(--bg-raised)] rounded border border-[var(--rule)]">
              <MessageSquareQuote className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0 mt-0.5" />
              <p className="text-xs italic text-[var(--text-sec)] line-clamp-2">
                "{(node as any).customerQuote}"
              </p>
            </div>
          )}
          
          {/* Status badges */}
          <div className="flex items-center gap-2 mt-2">
            {node.type === 'solution' && (
              <ValidationStatusBadge status={(node as any).validationStatus} />
            )}
            {node.type === 'experiment' && (
              <ExperimentStatusBadge status={(node as any).status} />
            )}
            {node.type === 'solution' && (node as any).confidence !== undefined && (
              <span className="text-xs text-[var(--text-ter)]">
                {(node as any).confidence}% confidence
              </span>
            )}
          </div>
        </div>
        
        {/* Add child button */}
        {canAddChildren && (
          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowAddMenu(!showAddMenu);
              }}
              className="p-1.5 hover:bg-[var(--bg-raised)]/50 transition-colors"
              title="Add child node"
            >
              <Plus className="h-4 w-4 text-[var(--text-ter)]" />
            </button>
            
            {/* Add child dropdown */}
            {showAddMenu && (
              <div className="absolute right-0 top-8 z-10 min-w-[220px] border border-[var(--rule)] bg-[var(--bg-raised)] py-1 shadow-lg dark:bg-[var(--surface)]">
                {node.type === 'outcome' && onPickStrategicObjective && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onPickStrategicObjective(node.id);
                      setShowAddMenu(false);
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm text-[var(--text-sec)] hover:bg-consulting-slate-blue-50 dark:text-[var(--text-pri)] dark:hover:bg-consulting-slate-blue-900/20"
                  >
                    <Flag className="h-4 w-4 text-consulting-slate-blue-600" />
                    <span>Strategic objective…</span>
                  </button>
                )}
                {node.type === 'outcome' && onPickDesiredOutcome && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onPickDesiredOutcome(node.id);
                      setShowAddMenu(false);
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm text-[var(--text-sec)] hover:bg-consulting-slate-blue-50 dark:text-[var(--text-pri)] dark:hover:bg-consulting-slate-blue-900/20"
                  >
                    <Crosshair className="h-4 w-4 text-consulting-slate-blue-600" />
                    <span>Desired outcome…</span>
                  </button>
                )}
                {allowedChildren[node.type].map(childType => {
                  const childConfig = nodeTypeConfig[childType];
                  const ChildIcon = childConfig.icon;
                  return (
                    <button
                      key={childType}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onAddChild(node.id, childType);
                        setShowAddMenu(false);
                      }}
                      className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm text-[var(--text-sec)] hover:bg-[var(--surface)] dark:text-[var(--text-pri)] dark:hover:bg-[var(--bg-raised)]/80"
                    >
                      <ChildIcon className={`h-4 w-4 ${childConfig.color}`} />
                      <span>Add {childConfig.label}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Children */}
      {hasChildren && !isCollapsed && (
        <div className="ml-8 mt-4 space-y-4 relative">
          {/* Vertical connection line */}
          <div className="absolute left-6 top-0 bottom-4 w-px bg-[var(--rule-bright)]" />
          
          {children.map((child, index) => (
            <div key={child.node.id} className="relative">
              {/* Horizontal connection line */}
              <div className="absolute -left-2 top-6 w-8 h-px bg-[var(--rule-bright)]" />
              
              <TreeNode
                treeNode={child}
                selectedNodeId={selectedNodeId}
                onSelectNode={onSelectNode}
                onAddChild={onAddChild}
                onToggleCollapse={onToggleCollapse}
                onPickStrategicObjective={onPickStrategicObjective}
                onPickDesiredOutcome={onPickDesiredOutcome}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function TreeVisualization({
  nodes,
  selectedNodeId,
  onSelectNode,
  onAddChild,
  onToggleCollapse,
  onPickStrategicObjective,
  onPickDesiredOutcome,
}: TreeVisualizationProps) {
  const tree = useMemo(() => buildTree(nodes), [nodes]);
  
  if (tree.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Target className="h-12 w-12 text-[var(--text-ter)] mb-4" />
        <h3 className="text-lg font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] mb-2">
          Start with a strategy
        </h3>
        <p className="text-[var(--text-sec)] dark:text-[var(--text-sec)] max-w-md text-sm">
          Add a strategy node as the root, then branch into opportunities, solutions, and experiments.
        </p>
      </div>
    );
  }
  
  return (
    <div className="p-6">
      <div className="space-y-6">
        {tree.map(rootNode => (
          <TreeNode
            key={rootNode.node.id}
            treeNode={rootNode}
            selectedNodeId={selectedNodeId}
            onSelectNode={onSelectNode}
            onAddChild={onAddChild}
            onToggleCollapse={onToggleCollapse}
            onPickStrategicObjective={onPickStrategicObjective}
            onPickDesiredOutcome={onPickDesiredOutcome}
          />
        ))}
      </div>
    </div>
  );
}

export default TreeVisualization;
